/**
 * Units (core units.ts, temperature.ts T7): "°F · °C" flips every temperature, weight, volume and length on screen.
 * Whose unit: this person's own (their profile, PUT /me/temp-unit), else this device's last tap, else the business's
 * default (Settings), else °F. On the shop tablet (kiosk) the device's own choice wins and nobody's profile is written.
 * Display only: stored readings are canonical °F with what was typed kept beside them; every rule compares °F.
 */
import { useCallback, useSyncExternalStore } from "react";
import { tempUnitFor, tempUnitOf, unitPrefOf, type TempUnit, type UnitPref } from "@logwave/core";
import { useApiMaybe, useSessionMaybe } from "../session.tsx";
import { useKiosk } from "./kiosk.ts";

export const UNITS_KEY = "cc.units";
/** Set once the first-time hint ("Tap °F or °C to match your thermometer") has been shown on this device. */
export const UNITS_HINT_KEY = "cc.units.hinted";

const subs = new Set<() => void>();
/** Where storage is blocked (private mode), the choice still holds for this tab. */
let memo: UnitPref | null = null;
/** A tap's choice for this person, until the next /me says the same (the profile write is in flight or offline). */
let personTap: TempUnit | null = null;
const emit = () => subs.forEach((f) => f());

/** This device's own choice, or null when nobody has tapped here yet. */
function readDevice(): UnitPref | null {
  try {
    const v = localStorage.getItem(UNITS_KEY);
    return v === "metric" ? "metric" : v === "us" ? "us" : null;
  } catch { return memo; }
}

/** Remember a choice on this device only (the step screens' chip, the kiosk). */
export function setUnits(pref: UnitPref) {
  memo = pref;
  try { localStorage.setItem(UNITS_KEY, pref === "metric" ? "metric" : "us"); } catch { /* private mode: `memo` keeps it */ }
  emit();
}

function subscribe(f: () => void) {
  subs.add(f);
  const onStorage = (e: StorageEvent) => { if (e.key === UNITS_KEY) f(); };
  window.addEventListener("storage", onStorage);
  return () => { subs.delete(f); window.removeEventListener("storage", onStorage); };
}
const snapshot = () => `${readDevice() ?? ""}|${personTap ?? ""}`;

/**
 * [unit, set] for the thermometer: the unit that applies here now, and the one tap that changes it. On a personal device the
 * tap is remembered here and on the person's profile (every business they're in); on the kiosk, here only.
 */
export function useTempUnit(): [TempUnit, (u: TempUnit) => void] {
  useSyncExternalStore(subscribe, snapshot, () => "|");
  const session = useSessionMaybe();
  const api = useApiMaybe();
  const kiosk = useKiosk();
  const shared = !!kiosk && (!session || kiosk.orgId === session.org.id);
  const mine = session?.me.memberships.find((m) => m.org.id === session.org.id)?.tempUnit ?? null;
  const device = readDevice();
  const unit = tempUnitFor({ person: personTap ?? mine, device: device ? tempUnitOf(device) : null, business: session?.org.tempUnit ?? null, shared });
  const set = useCallback((u: TempUnit) => {
    setUnits(unitPrefOf(u));
    if (shared || !session || !api) return;
    personTap = u;
    emit();
    // Offline or refused: the device keeps the choice; the profile catches up on the next tap.
    void Promise.resolve().then(() => api.setMyTempUnit(u)).catch(() => undefined);
  }, [shared, session, api]);
  return [unit, set];
}

/** The display preference the step screens use (core units.ts): °C = metric. */
export function useUnits(): UnitPref {
  return unitPrefOf(useTempUnit()[0]);
}

/** True the first time this device shows a temperature toggle; marks it shown. */
export function firstToggleHint(): boolean {
  try {
    if (localStorage.getItem(UNITS_HINT_KEY)) return false;
    localStorage.setItem(UNITS_HINT_KEY, "1");
    return true;
  } catch { return false; }
}

/** For tests: forget the in-memory tap. */
export function resetUnitsForTest() { memo = null; personTap = null; emit(); }
